
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const navigate = useNavigate();
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);

      if (location.pathname === '/') {
        const sections = ['hero', 'about', 'services', 'products', 'clients', 'contact'];
        for (const section of sections) {
          const element = document.getElementById(section);
          if (element) {
            const rect = element.getBoundingClientRect();
            // Offset for fixed header
            if (rect.top <= 150 && rect.bottom >= 150) {
              setActiveSection(section);
              break;
            }
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    // Trigger once on mount to set initial state
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileOpen && !event.target.closest('.mobile-menu')) {
        setMobileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId || 'hero');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId || 'hero');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setMobileOpen(false);
  };

  const navItems = [
    { label: 'Home', section: 'hero', path: '/' },
    { label: 'About', section: 'about', path: '/' },
    { label: 'Services', section: 'services', path: '/' },
    { label: 'Products', section: 'products', path: '/' },
    { label: 'Clients', section: 'clients', path: '/' },
    { label: 'Catalog', action: () => navigate('/catalog'), path: '/catalog' },
    { label: 'Contact', section: 'contact', path: '/' },
  ];

  // Function to check if nav item is active
  const isActiveNavItem = (item) => {
    if (item.path === '/catalog' && location.pathname === '/catalog') {
      return true;
    }
    if (item.path === '/' && location.pathname === '/') {
      if (item.section) {
        return activeSection === item.section;
      }
    }
    return false;
  };

  const toggleMobileMenu = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <>
      {/* Fixed Header */}
      <motion.header 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 w-full ${
          scrolled 
            ? 'bg-dark/95 backdrop-blur-xl shadow-md border-b border-white/5' 
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20 w-full">
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="cursor-pointer group flex-shrink-0 min-w-0"
              onClick={() => navigate('/')}
            >
              <div className="flex items-center gap-3">
                <img 
                  src="/logo.jpeg" 
                  alt="Kashyap Engineering Logo" 
                  className="h-10 w-10 sm:h-12 sm:w-12 lg:h-14 lg:w-14 object-contain group-hover:scale-105 transition-transform duration-300"
                />
              </div>
            </motion.div>

            {/* Desktop Navigation & CTA */}
            <div className="hidden lg:flex items-center space-x-6">
              <nav className="flex items-center space-x-1 xl:space-x-4">
              {navItems.map((item, index) => {
                const isActive = isActiveNavItem(item);
                return (
                  <motion.button
                    key={item.section || item.label}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    onClick={() => item.action ? item.action() : scrollToSection(item.section)}
                    className={`relative px-2 py-2 font-semibold transition-all duration-300 group overflow-hidden text-white hover:text-primary text-sm xl:text-base ${
                      isActive ? 'text-primary' : ''
                    }`}
                  >
                    
                    {/* Text */}
                    <span className="relative z-10 flex items-center gap-1">
                      {item.label}
                      {item.section && <ChevronDown className="w-3 h-3 xl:w-4 xl:h-4 opacity-60 group-hover:opacity-100 transition-opacity" />}
                    </span>
                    
                    {/* Underline effect */}
                    <div className={`absolute bottom-0 left-1/2 h-0.5 bg-primary transition-all duration-300 ${
                      isActive ? 'w-full left-0' : 'w-0 group-hover:w-full group-hover:left-0'
                    }`}></div>
                  </motion.button>
                );
              })}
              </nav>
              {/* CTA Button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => scrollToSection('contact')}
                className="bg-primary hover:bg-primary-600 text-white font-semibold px-6 py-2 rounded-md shadow-glow-red transition-all duration-300 ml-4"
              >
                Get a Quote
              </motion.button>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center flex-shrink-0">
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={toggleMobileMenu}
                className="p-2 rounded-2xl transition-all duration-300 hover:scale-105 text-white hover:bg-white/10"
                aria-label="Toggle mobile menu"
              >
                <Menu className="w-5 h-5" />
              </motion.button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
            
            {/* Mobile Menu */}
            <motion.div
              initial={{ x: '100%', opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: '100%', opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="mobile-menu fixed top-0 right-0 bottom-0 w-72 max-w-[80vw] z-50 glass border-l border-gray-700/20"
            >
              {/* Mobile Menu Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-700/10">
                <h3 className="text-lg font-bold text-gradient">
                  Menu
                </h3>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setMobileOpen(false)}
                  className="p-2 rounded-xl transition-colors text-white hover:bg-white/10"
                >
                  <X className="w-5 h-5" />
                </motion.button>
              </div>

              {/* Mobile Menu Items */}
              <nav className="p-4 space-y-1">
                {navItems.map((item, index) => {
                  const isActive = isActiveNavItem(item);
                  return (
                    <motion.button
                      key={item.section || item.label}
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 50 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      onClick={() => item.action ? item.action() : scrollToSection(item.section)}
                      className={`w-full text-left px-4 py-3 rounded-2xl font-semibold transition-all duration-300 hover:translate-x-2 group text-base ${
                        isActive 
                          ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-lg' 
                          : 'text-white hover:bg-white/10'
                      }`}
                    >
                      <span className="flex items-center justify-between">
                        {item.label}
                        {item.section && (
                          <ChevronDown className="w-4 h-4 opacity-60 group-hover:opacity-100 transition-opacity" />
                        )}
                      </span>
                    </motion.button>
                  );
                })}
              </nav>

              {/* Mobile Menu Footer */}
              <div className="absolute bottom-4 left-4 right-4">
                <div className="p-3 rounded-2xl bg-white/10 border border-white/20">
                  <p className="text-sm font-medium mb-1 text-white">
                    Kashyap Engineering
                  </p>
                  <p className="text-xs text-gray-400">
                    Leading Fire Safety Solutions
                  </p>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Header Spacer */}
      <div className="h-20 sm:h-24"></div>
    </>
  );
};

export default Header;
